import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/** Figma's font size scale, emitted as `--text-<step>` by the token generator. */
const DS_FONT_SIZE_STEPS = [
  '100',
  '200',
  '300',
  '400',
  '500',
  '550',
  '600',
  '700',
  '800',
  '900',
  '1000',
  '1100',
  '1200',
  '1300',
  '1400',
];

const isDsToken = (value: string) => value.startsWith('pmui-');

/**
 * tailwind-merge only knows Tailwind's own class names, so every design-system token
 * has to be taught to it:
 *
 * - `font-body` / `font-heading` are families; `font-normal`, `font-emphasis`,
 *   `font-accent` and `font-strong` are weights. Without the split, a family and a
 *   weight look like the same group and one of them is dropped.
 * - the font size scale is numeric (`text-400`), which is not a t-shirt size, so it
 *   has to be claimed by `font-size` before the text-colour group matches it.
 * - `leading-pmui-*` and `tracking-pmui-*` carry the `pmui-` namespace that keeps the
 *   DS scales from colliding with Tailwind's own `leading-*` / `tracking-*` steps.
 * - `conflictingClassGroups` is cleared for `font-size` because the DS `text-*` scale
 *   sets font-size alone, so it must not clear a `leading-*` set alongside it.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-family': [{ font: ['body', 'heading'] }],
      'font-weight': [{ font: ['default', 'emphasis', 'strong', 'accent'] }],
      'font-size': [{ text: DS_FONT_SIZE_STEPS }],
      leading: [{ leading: [isDsToken] }],
      tracking: [{ tracking: [isDsToken] }],
    },
  },
  override: {
    conflictingClassGroups: { 'font-size': [] },
  },
});

/**
 * Utility function to merge Tailwind CSS classes with proper conflict resolution
 * Combines clsx for conditional classes and tailwind-merge for deduplication
 *
 * @example
 * cn('px-4 py-2', condition && 'bg-blue-500', 'px-6') // 'py-2 bg-blue-500 px-6'
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
